// AuthPage.js — iXL Martial Arts CRM Login
import React, { useState } from "react";
import { auth, database, ref, set, get } from "./firebase";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut
} from "firebase/auth";

/* =====================================================
   STYLES
   ===================================================== */

const PAGE_STYLE = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #0a1628 0%, #1a2744 40%, #0d3b66 100%)",
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  padding: 20,
  position: "relative",
  overflow: "hidden",
};

const CARD_STYLE = {
  background: "#fff",
  borderRadius: 16,
  padding: "40px 36px 36px",
  width: "100%",
  maxWidth: 420,
  boxShadow: "0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)",
  position: "relative",
  zIndex: 1,
};

const LOGO_SECTION = {
  textAlign: "center",
  marginBottom: 28,
};

const LOGO_IMG = {
  height: 80,
  objectFit: "contain",
  marginBottom: 12,
};

const BRAND_TITLE = {
  fontSize: 22,
  fontWeight: 700,
  color: "#1a2744",
  margin: "0 0 4px 0",
  letterSpacing: "-0.3px",
};

const BRAND_SUB = {
  fontSize: 13,
  color: "#8896a8",
  margin: 0,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "1.5px",
};

const TAB_ROW = {
  display: "flex",
  background: "#f0f2f5",
  borderRadius: 10,
  padding: 4,
  marginBottom: 28,
};

const tabStyle = (active) => ({
  flex: 1,
  padding: "10px 0",
  fontSize: 14,
  fontWeight: 600,
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.2s ease",
  background: active ? "#fff" : "transparent",
  color: active ? "#1a2744" : "#8896a8",
  boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
});

const LABEL_STYLE = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#4a5568",
  marginBottom: 6,
};

const INPUT_STYLE = {
  width: "100%",
  padding: "12px 14px",
  fontSize: 15,
  border: "2px solid #e2e8f0",
  borderRadius: 10,
  outline: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  boxSizing: "border-box",
  color: "#1a2744",
  background: "#fafbfc",
};

const INPUT_FOCUS = {
  borderColor: "#2563eb",
  boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.12)",
  background: "#fff",
};

const SUBMIT_BTN = {
  width: "100%",
  padding: "13px 0",
  fontSize: 15,
  fontWeight: 700,
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
  background: "linear-gradient(135deg, #1e40af 0%, #2563eb 100%)",
  color: "#fff",
  transition: "all 0.2s ease",
  boxShadow: "0 2px 8px rgba(37, 99, 235, 0.35)",
  letterSpacing: "0.3px",
  marginTop: 4,
};

const SUBMIT_BTN_DISABLED = {
  ...SUBMIT_BTN,
  opacity: 0.6,
  cursor: "not-allowed",
};

const LINK_BTN = {
  background: "none",
  border: "none",
  color: "#2563eb",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 500,
  padding: 0,
  transition: "color 0.2s",
  textDecoration: "underline",
  appearance: "none",
  WebkitAppearance: "none",
};

const ERROR_BOX = {
  background: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#dc2626",
  padding: "10px 14px",
  borderRadius: 8,
  fontSize: 13,
  lineHeight: 1.5,
};

const SUCCESS_BOX = {
  background: "#f0fdf4",
  border: "1px solid #bbf7d0",
  color: "#16a34a",
  padding: "10px 14px",
  borderRadius: 8,
  fontSize: 13,
  lineHeight: 1.5,
};

const DIVIDER = {
  height: 1,
  background: "#e2e8f0",
  margin: "20px 0",
  border: "none",
};

/* Decorative background elements */
const BG_CIRCLE_1 = {
  position: "absolute",
  width: 500,
  height: 500,
  borderRadius: "50%",
  background: "radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%)",
  top: -200,
  right: -100,
  pointerEvents: "none",
};

const BG_CIRCLE_2 = {
  position: "absolute",
  width: 400,
  height: 400,
  borderRadius: "50%",
  background: "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
  bottom: -150,
  left: -100,
  pointerEvents: "none",
};

/* =====================================================
   COMPONENT
   ===================================================== */

function FocusInput({ label, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={LABEL_STYLE}>{label}</label>}
      <input
        {...props}
        style={{ ...INPUT_STYLE, ...(focused ? INPUT_FOCUS : {}) }}
        onFocus={(e) => { setFocused(true); if (props.onFocus) props.onFocus(e); }}
        onBlur={(e) => { setFocused(false); if (props.onBlur) props.onBlur(e); }}
      />
    </div>
  );
}

export default function AuthPage({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [registering, setRegistering] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);
  const [registerAs, setRegisterAs] = useState("student"); // "student" or "staff"
  const [relation, setRelation] = useState("student"); // portal signup: "student" or "parent"
  const [studentFor, setStudentFor] = useState("");     // parent signup: the student's name
  const [pendingSignup, setPendingSignup] = useState(false);

  const handleRegister = async () => {
    setErr("");
    setJustRegistered(false);
    if (!name.trim()) { setErr("Please enter your full name."); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (password !== confirmPassword) { setErr("Passwords do not match."); return; }
    try {
      setRegistering(true);
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCred.user, { displayName: name });
      try {
        const isStudent = registerAs === "student";

        // Student flow: match email against student records for family linking
        let linkedStudents = [];
        if (isStudent) {
          const studentsSnap = await get(ref(database, "students"));
          if (studentsSnap.exists()) {
            const allStudents = studentsSnap.val();
            const normalizedEmail = email.trim().toLowerCase();
            Object.entries(allStudents).forEach(([studentId, student]) => {
              const info = student.extraInfo || {};
              const emails = [
                (info.email || "").trim().toLowerCase(),
                (info.parent1Email || "").trim().toLowerCase(),
                (info.parent2Email || "").trim().toLowerCase(),
              ];
              if (emails.some((e) => e && e === normalizedEmail)) {
                linkedStudents.push(studentId);
              }
            });
          }

          // No matching student record: keep the account, but it waits for the school
          // to connect it to the right student (Settings > Portal Accounts).
        }
        const pending = isStudent && linkedStudents.length === 0;

        const userData = isStudent
          ? {
              name,
              email,
              role: "student",
              linkedStudents,
              activeStudentId: linkedStudents[0] || null,
              createdAt: new Date().toISOString(),
              ...(pending ? {
                pendingApproval: true,
                signupInfo: { relation, studentName: relation === "parent" ? studentFor.trim() : name.trim() },
              } : {}),
            }
          : {
              name,
              email,
              isAdmin: false,
              approved: false,
            };
        await set(ref(database, `users/${userCred.user.uid}`), userData);
        // Student: send verification email (App.js will show verification screen)
        // Staff: sign out to await admin approval
        if (pending) {
          fetch("/.netlify/functions/send-portal-signup-alert", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name, email, relation, studentName: relation === "parent" ? studentFor.trim() : "", origin: window.location.origin }),
          }).catch(() => {});
        }
        setPendingSignup(pending);
        if (isStudent) {
          // Branded email through Brevo; Firebase's own mailer is the backup
          try {
            const idToken = await userCred.user.getIdToken();
            const vres = await fetch("/.netlify/functions/portal-verify-email", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ idToken, action: "send", origin: window.location.origin }),
            });
            if (!vres.ok) throw new Error("send failed");
          } catch (ve) {
            await sendEmailVerification(userCred.user).catch(() => {});
          }
          // Don't sign out — App.js will catch unverified student and show verification screen
        } else {
          await signOut(auth);
        }
        setJustRegistered(true);
        setErr("");
        setRegistering(false);
        setName(""); setEmail(""); setPassword(""); setConfirmPassword(""); setStudentFor("");
      } catch (dbErr) {
        await userCred.user.delete();
        setErr("Registration failed: Database access error. Please contact admin.");
        setRegistering(false);
      }
    } catch (error) {
      let msg = error.message;
      if (msg.includes("auth/email-already-in-use")) msg = "An account with this email already exists.";
      else if (msg.includes("auth/invalid-email")) msg = "Please enter a valid email address.";
      else if (msg.includes("auth/weak-password")) msg = "Password must be at least 6 characters.";
      setErr(msg);
      setRegistering(false);
    }
  };

  const handleLogin = async () => {
    setErr("");
    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      if (typeof onLogin === "function") onLogin(userCred.user);
    } catch (error) {
      let msg = error.message;
      if (msg.includes("auth/invalid-credential") || msg.includes("auth/wrong-password") || msg.includes("auth/user-not-found")) {
        msg = "Invalid email or password. Please try again.";
      } else if (msg.includes("auth/too-many-requests")) {
        msg = "Too many attempts. Please wait a moment and try again.";
      }
      setErr(msg);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetMsg("");
    setErr("");
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMsg("Password reset email sent! Check your inbox.");
    } catch (error) {
      let msg = error.message;
      if (msg.includes("auth/user-not-found")) msg = "No account found with that email.";
      setErr(msg);
    }
  };

  /* ---------- RENDER ---------- */

  return (
    <div style={PAGE_STYLE}>
      {/* Decorative background */}
      <div style={BG_CIRCLE_1} />
      <div style={BG_CIRCLE_2} />

      <div style={CARD_STYLE}>
        {/* Logo & Branding */}
        <div style={LOGO_SECTION}>
          <img
            src="/ninja-vertical.png"
            alt="iXL Ninja"
            style={LOGO_IMG}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <h1 style={BRAND_TITLE}>iXL Ninja</h1>
          <p style={BRAND_SUB}>Management Portal</p>
        </div>

        {/* Login / Register Tabs */}
        {!showReset && !justRegistered && (
          <div style={TAB_ROW}>
            <button
              style={tabStyle(mode === "login")}
              onClick={() => { setMode("login"); setErr(""); }}
              type="button"
            >
              Sign In
            </button>
            <button
              style={tabStyle(mode === "register")}
              onClick={() => { setMode("register"); setErr(""); }}
              type="button"
            >
              Register
            </button>
          </div>
        )}

        {/* ===== REGISTRATION SUCCESS ===== */}
        {justRegistered && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>{"\u2705"}</div>
              <h3 style={{ margin: "0 0 8px", color: "#1a2744" }}>
                {registerAs === "student" ? "Check Your Email" : "Registration Submitted"}
              </h3>
              <p style={{ color: "#64748b", fontSize: 14, lineHeight: 1.6, margin: 0 }}>
                {registerAs === "student" && pendingSignup
                  ? "We've sent a verification link to your email. Your email isn't on file with us yet, so iXL will connect your account to your student profile and let you know. You can verify your email now."
                  : registerAs === "student"
                  ? "We've sent a verification link to your email. Click the link to verify your account, then come back here to sign in."
                  : "Your account is pending approval. An administrator will review and activate your account shortly."}
              </p>
            </div>
            <hr style={DIVIDER} />
            <button
              style={{ ...LINK_BTN, width: "100%", textAlign: "center" }}
              onClick={() => { setJustRegistered(false); setMode("login"); }}
            >
              {"\u2190"} Back to Sign In
            </button>
          </div>
        )}

        {/* ===== PASSWORD RESET ===== */}
        {!justRegistered && showReset && (
          <div>
            <h3 style={{ margin: "0 0 6px", color: "#1a2744", fontSize: 18 }}>Reset Password</h3>
            <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 20px" }}>
              Enter your email and we'll send you a reset link.
            </p>
            <form onSubmit={handlePasswordReset}>
              <FocusInput
                label="Email Address"
                type="email"
                placeholder="you@example.com"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                required
                autoFocus
              />
              {resetMsg && <div style={{ ...SUCCESS_BOX, marginBottom: 16 }}>{resetMsg}</div>}
              {err && <div style={{ ...ERROR_BOX, marginBottom: 16 }}>{err}</div>}
              <button type="submit" style={SUBMIT_BTN}>
                Send Reset Link
              </button>
            </form>
            <hr style={DIVIDER} />
            <button
              style={{ ...LINK_BTN, width: "100%", textAlign: "center" }}
              onClick={() => { setShowReset(false); setErr(""); setResetMsg(""); }}
            >
              {"\u2190"} Back to Sign In
            </button>
          </div>
        )}

        {/* ===== LOGIN / REGISTER FORMS ===== */}
        {!justRegistered && !showReset && (
          <form
            onSubmit={e => {
              e.preventDefault();
              mode === "login" ? handleLogin() : handleRegister();
            }}
            autoComplete="on"
          >
            {mode === "register" && (
              <div style={{ marginBottom: 20 }}>
                <label style={LABEL_STYLE}>I am a...</label>
                <div style={{
                  display: "flex",
                  background: "#f0f2f5",
                  borderRadius: 10,
                  padding: 4,
                }}>
                  <button
                    type="button"
                    onClick={() => setRegisterAs("student")}
                    style={{
                      flex: 1,
                      padding: "10px 0",
                      fontSize: 14,
                      fontWeight: 600,
                      border: "none",
                      borderRadius: 8,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      background: registerAs === "student" ? "#fff" : "transparent",
                      color: registerAs === "student" ? "#0080C6" : "#8896a8",
                      boxShadow: registerAs === "student" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    }}
                  >
                    Student / Parent
                  </button>
                  <button
                    type="button"
                    onClick={() => setRegisterAs("staff")}
                    style={{
                      flex: 1,
                      padding: "10px 0",
                      fontSize: 14,
                      fontWeight: 600,
                      border: "none",
                      borderRadius: 8,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      background: registerAs === "staff" ? "#fff" : "transparent",
                      color: registerAs === "staff" ? "#1a2744" : "#8896a8",
                      boxShadow: registerAs === "staff" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    }}
                  >
                    Staff Member
                  </button>
                </div>
              </div>
            )}
            {mode === "register" && (
              <FocusInput
                label="Full Name"
                type="text"
                placeholder="John Smith"
                value={name}
                onChange={e => setName(e.target.value)}
                autoComplete="name"
              />
            )}
            {mode === "register" && registerAs === "student" && (
              <div style={{ marginBottom: 16 }}>
                <label style={LABEL_STYLE}>I am the</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[["student", "Student"], ["parent", "Parent or guardian"]].map(([k, t]) => (
                    <button key={k} type="button" onClick={() => setRelation(k)}
                      style={{ flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer",
                        border: relation === k ? "2px solid #0080C6" : "1px solid #d8dee8", background: relation === k ? "#f0f8ff" : "#fff", color: relation === k ? "#0080C6" : "#64748b" }}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {mode === "register" && registerAs === "student" && relation === "parent" && (
              <FocusInput
                label="Student's Full Name"
                type="text"
                placeholder="Your child's name"
                value={studentFor}
                onChange={e => setStudentFor(e.target.value)}
              />
            )}
            <FocusInput
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="username"
            />
            <FocusInput
              label="Password"
              type="password"
              placeholder={mode === "login" ? "Enter your password" : "Create a password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
            {mode === "register" && (
              <FocusInput
                label="Confirm Password"
                type="password"
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            )}

            {err && <div style={{ ...ERROR_BOX, marginBottom: 16 }}>{err}</div>}

            <button
              type="submit"
              disabled={registering}
              style={registering ? SUBMIT_BTN_DISABLED : SUBMIT_BTN}
            >
              {mode === "login" ? "Sign In" : registering ? "Creating Account..." : registerAs === "student" ? "Create Portal Account" : "Create Staff Account"}
            </button>

            {mode === "login" && (
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <span
                  role="button"
                  tabIndex={0}
                  style={LINK_BTN}
                  onClick={() => { setShowReset(true); setErr(""); setResetMsg(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { setShowReset(true); setErr(""); setResetMsg(""); } }}
                >
                  Forgot your password?
                </span>
              </div>
            )}
          </form>
        )}

        {/* Footer */}
        <div style={{
          textAlign: "center",
          marginTop: 24,
          paddingTop: 20,
          borderTop: "1px solid #f1f5f9",
          color: "#94a3b8",
          fontSize: 12,
        }}>
          {"\u00A9"} {new Date().getFullYear()} iXL Ninja. All rights reserved.
        </div>
      </div>
    </div>
  );
}
